import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { captureExceptionWithContext } from '@/config/sentry'
import { logger } from '@/utils/logger'
import * as Sentry from '@sentry/react'
import { AlertCircle, Bug, ChevronDown, ChevronUp, Home } from 'lucide-react'
import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, errorInfo: ErrorInfo, retry: () => void) => ReactNode
  showDialog?: boolean
  level?: 'page' | 'section' | 'component'
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string | null
  isDetailsExpanded: boolean
  retryCount: number
}

export class SentryErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isDetailsExpanded: false,
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { level = 'component', onError } = this.props
    const { retryCount } = this.state

    // Generate a unique error ID for tracking
    const errorId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Log the error locally
    logger.error(`Error caught by ${level} boundary`, {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo,
      level,
      retryCount,
    })

    // Capture to Sentry with additional context
    captureExceptionWithContext(error, {
      errorBoundary: {
        level,
        retryCount,
        componentStack: errorInfo.componentStack,
      },
      tags: {
        errorBoundaryLevel: level,
        errorId,
      },
    })

    // Update state
    this.setState({
      errorInfo,
      errorId,
    })

    // Call the onError callback if provided
    if (onError) {
      onError(error, errorInfo)
    }

    // Show Sentry user feedback dialog if enabled
    if (this.props.showDialog) {
      Sentry.showReportDialog({
        eventId: errorId,
        title: 'An error occurred',
        subtitle: 'Our team has been notified. If you\'d like to help, tell us what happened below.',
        subtitle2: '',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit',
        successMessage: 'Thank you for your feedback!',
        onLoad: () => {
          logger.info('Sentry feedback dialog opened', { errorId })
        },
        onClose: () => {
          logger.info('Sentry feedback dialog closed', { errorId })
        },
      })
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }
  }

  handleReset = () => {
    const { retryCount } = this.state

    // Log the retry attempt
    logger.info('Error boundary reset attempted', { 
      retryCount: retryCount + 1,
      errorId: this.state.errorId,
    })

    // Add breadcrumb for the retry
    Sentry.addBreadcrumb({
      category: 'error-boundary',
      message: 'User attempted error recovery',
      level: 'info',
      data: {
        retryCount: retryCount + 1,
        errorId: this.state.errorId,
      },
    })

    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isDetailsExpanded: false,
      retryCount: retryCount + 1,
    })

    // Auto-reset retry count after 5 minutes
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }
    this.resetTimeoutId = setTimeout(() => {
      this.setState({ retryCount: 0 })
    }, 5 * 60 * 1000)
  }

  handleGoHome = () => {
    // Add breadcrumb for navigation
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: 'User navigated home from error boundary',
      level: 'info',
      data: {
        errorId: this.state.errorId,
      },
    })

    window.location.href = '/dashboard'
  }

  toggleDetails = () => {
    this.setState(prev => ({ isDetailsExpanded: !prev.isDetailsExpanded }))
  }

  render() {
    const { hasError, error, errorInfo, errorId, isDetailsExpanded, retryCount } = this.state
    const { children, fallback, level = 'component' } = this.props

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!, this.handleReset)
      }

      // Default fallback UI based on error boundary level
      const isPageLevel = level === 'page'
      const containerClass = isPageLevel 
        ? 'min-h-screen flex items-center justify-center bg-background p-4'
        : 'flex items-center justify-center p-8'

      return (
        <div className={containerClass}>
          <Card className="max-w-lg w-full p-6 shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                <AlertCircle className="w-16 h-16 text-destructive mb-4" />
                {retryCount > 2 && (
                  <Bug className="w-6 h-6 text-orange-500 absolute -bottom-1 -right-1" />
                )}
              </div>
              
              <h2 className="text-2xl font-bold mb-2">
                {retryCount > 2 ? 'Persistent Error Detected' : 'Something went wrong'}
              </h2>
              
              <p className="text-muted-foreground mb-4">
                {error.message || 'An unexpected error occurred'}
              </p>

              {retryCount > 0 && (
                <p className="text-sm text-muted-foreground mb-4">
                  Retry attempt: {retryCount}
                  {retryCount > 2 && ' - Consider refreshing the page'}
                </p>
              )}

              {errorId && (
                <p className="text-xs text-muted-foreground mb-6 font-mono">
                  Error ID: {errorId}
                </p>
              )}

              <div className="flex gap-3 mb-6">
                <Button
                  onClick={this.handleReset}
                  variant="default"
                  className="flex items-center gap-2"
                  disabled={retryCount > 5}
                >
                  
                  {retryCount > 5 ? 'Max retries reached' : 'Try Again'}
                </Button>

                {isPageLevel && (
                  <Button
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Go to Dashboard
                  </Button>
                )}
              </div>

              {/* Error details section */}
              {import.meta.env.DEV && (
                <div className="w-full">
                  <button
                    onClick={this.toggleDetails}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-left bg-muted rounded-md hover:bg-muted/80 transition-colors"
                  >
                    <span>Error Details</span>
                    {isDetailsExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {isDetailsExpanded && (
                    <div className="mt-2 space-y-2">
                      <div className="p-3 bg-muted rounded-md">
                        <p className="text-xs font-semibold mb-1">Error Stack:</p>
                        <pre className="text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                          {error.stack}
                        </pre>
                      </div>

                      {errorInfo && (
                        <div className="p-3 bg-muted rounded-md">
                          <p className="text-xs font-semibold mb-1">Component Stack:</p>
                          <pre className="text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Helpful tips for users */}
              {retryCount > 2 && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md w-full">
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>Tip:</strong> Try refreshing the page or clearing your browser cache if the error persists.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )
    }

    return children
  }
}

// Higher-order component for easy wrapping
export const withSentryErrorBoundary = Sentry.withErrorBoundary

// Hook for programmatic error handling
export const useSentryErrorHandler = () => {
  return (error: Error, errorInfo?: { componentStack?: string }) => {
    captureExceptionWithContext(error, {
      errorInfo,
      source: 'manual',
    })
  }
}

// Convenience wrapper that combines our custom error boundary with Sentry's
export const EnhancedSentryErrorBoundary: React.FC<Props> = ({ children, onError, ...props }) => {
  const handleError = (error: unknown, componentStack: string | undefined, _eventId: string) => {
    if (onError && error instanceof Error) {
      onError(error, { componentStack: componentStack || '' })
    }
  }
  
  return (
    <Sentry.ErrorBoundary 
      fallback={({ error: _error, resetError: _resetError }) => (
        <SentryErrorBoundary {...props}>
          {children}
        </SentryErrorBoundary>
      )}
      onError={handleError}
      showDialog={props.showDialog}
    >
      <SentryErrorBoundary {...props} {...(onError && { onError })}>
        {children}
      </SentryErrorBoundary>
    </Sentry.ErrorBoundary>
  )
}